<template>
    <div class="survey-results">
        <div class="survey-results__header">
            <h1>{{ $t('surveys.results.title') }}</h1>
            <v-spacer></v-spacer>
            <v-btn
                outlined
                color="primary"
                @click="$router.push({ name: 'surveys' })"
            >
                <v-icon left>mdi-arrow-left</v-icon>
                {{ $t('surveys.results.back') }}
            </v-btn>
        </div>

        <div v-if="overview" class="survey-results__overview">
            <h2 class="survey-results__title">{{ overview.title }}</h2>
            <v-row>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="pa-4 text-center rounded-lg">
                        <div class="text-h5 font-weight-bold primary--text">
                            {{ overview.totalResponses || 0 }}
                        </div>
                        <div class="text-subtitle-2">{{ $t('surveys.results.overview.responses') }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="pa-4 text-center rounded-lg">
                        <div class="text-h5 font-weight-bold primary--text">
                            {{ overview.totalParticipants || 0 }}
                        </div>
                        <div class="text-subtitle-2">{{ $t('surveys.results.overview.participants') }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="pa-4 text-center rounded-lg">
                        <div class="text-h5 font-weight-bold primary--text">
                            {{ overview.participationRate | formatNumber }}%
                        </div>
                        <div class="text-subtitle-2">{{ $t('surveys.results.overview.participationRate') }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="pa-4 text-center rounded-lg">
                        <div class="text-h5 font-weight-bold primary--text">
                            {{ overview.totalQuestions || 0 }}
                        </div>
                        <div class="text-subtitle-2">{{ $t('surveys.results.overview.questions') }}</div>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <v-tabs v-model="tab" class="mt-6" background-color="transparent" color="primary" grow>
            <v-tab>{{ $t('surveys.results.tabs.summary') }}</v-tab>
            <v-tab>{{ $t('surveys.results.tabs.individual') }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" class="mt-4">
            <v-tab-item>
                <v-card v-if="!summary" outlined class="pa-4">
                    {{ $t('surveys.results.summary.noData') }}
                </v-card>
                <div v-else>
                    <v-card
                        v-for="(question, index) in summary.questions"
                        :key="index"
                        outlined
                        class="mb-4 pa-4 rounded-lg"
                    >
                        <div class="question-header">
                            <span class="question-number">{{ index + 1 }}</span>
                            <span class="font-weight-bold">{{ question.title }}</span>
                            <v-chip x-small class="ml-2" color="grey lighten-3">
                                {{ $t(`surveys.questionTypes.${question.type}.name`) }}
                            </v-chip>
                        </div>

                        <div v-if="question.distribution">
                            <div
                                v-for="(item, i) in question.distribution"
                                :key="i"
                                class="distribution-row"
                            >
                                <span class="distribution-row__label"><span class="option-number">{{ i + 1 }}.</span> {{ item.label }}</span>
                                <v-progress-linear
                                    :value="item.percentage"
                                    height="18"
                                    rounded
                                    class="distribution-row__bar"
                                    color="primary"
                                >
                                    <span class="white--text text-caption">
                                        {{ item.count }} ({{ item.percentage | formatNumber }}%)
                                    </span>
                                </v-progress-linear>
                            </div>
                        </div>

                        <div v-else-if="question.average !== null && question.average !== undefined" class="mb-2">
                            <strong>{{ $t('surveys.results.summary.average') }}:</strong> {{ question.average | formatNumber }}
                        </div>

                        <div v-else-if="question.nps" class="mb-2">
                            <div><strong>{{ $t('surveys.results.summary.npsScore') }}:</strong> {{ question.nps.score | formatNumber }}</div>
                            <div class="text-subtitle-2">
                                {{ $t('surveys.results.summary.promoters') }}: {{ question.nps.promoters }} |
                                {{ $t('surveys.results.summary.passives') }}: {{ question.nps.passives }} |
                                {{ $t('surveys.results.summary.detractors') }}: {{ question.nps.detractors }}
                            </div>
                        </div>

                        <div v-else-if="question.sampleResponses && question.sampleResponses.length" class="mb-2">
                            <div class="text-subtitle-2 font-weight-bold mb-1">{{ $t('surveys.results.summary.sampleResponses') }}:</div>
                            <div class="sample-responses">
                                <ul>
                                    <li v-for="(resp, i) in showSampleResponses(question)" :key="i">{{ resp }}</li>
                                </ul>
                            </div>
                            <v-btn
                                v-if="canShowMore(question)"
                                text
                                x-small
                                color="primary"
                                @click="toggleSample(question.questionId)"
                            >
                                {{ expandedSamples[question.questionId] ? $t('surveys.results.summary.showLess') : $t('surveys.results.summary.showMore') }}
                                <v-icon right x-small>{{ expandedSamples[question.questionId] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </v-btn>
                        </div>

                        <div v-else-if="question.histogram && question.histogram.length">
                            <div
                                v-for="(item, i) in question.histogram"
                                :key="i"
                                class="distribution-row"
                            >
                                <span class="distribution-row__label">{{ item.value }}</span>
                                <v-progress-linear
                                    :value="histogramPercent(item.count, question.histogram)"
                                    height="18"
                                    rounded
                                    class="distribution-row__bar"
                                    color="primary"
                                >
                                    <span class="white--text text-caption">{{ item.count }}</span>
                                </v-progress-linear>
                            </div>
                        </div>
                    </v-card>
                </div>
            </v-tab-item>

            <v-tab-item>
                <v-card outlined class="pa-4 mb-4 rounded-lg">
                    <v-row align="end">
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="fromDate"
                                :label="$t('surveys.results.individual.filters.from')"
                                :min="minFromDate"
                                :error-messages="fromDateErrors"
                                type="date"
                                outlined
                                dense
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="toDate"
                                :label="$t('surveys.results.individual.filters.to')"
                                :min="minToDate"
                                :error-messages="toDateErrors"
                                type="date"
                                outlined
                                dense
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4" class="d-flex">
                            <v-btn color="primary" @click="applyFilters" class="mr-2">
                                <v-icon left>mdi-magnify</v-icon>
                                {{ $t('surveys.results.individual.filters.filter') }}
                            </v-btn>
                            <v-btn outlined @click="resetFilters">
                                {{ $t('surveys.results.individual.filters.clear') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row class="mt-2">
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="personSearch"
                                :label="$t('surveys.results.individual.filters.searchPerson')"
                                :placeholder="$t('surveys.results.individual.filters.searchPersonPlaceholder')"
                                append-icon="mdi-magnify"
                                outlined
                                dense
                                hide-details
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-card>

                <v-card v-if="!responses.length" outlined class="pa-4 rounded-lg">
                    {{ $t('surveys.results.individual.noData') }}
                </v-card>
                <v-data-table
                    v-else
                    :headers="responseHeaders"
                    :items="responses"
                    :search="personSearch"
                    :items-per-page="10"
                    :footer-props="{ 'items-per-page-text': $t('label.rowsPerPage.title') }"
                    class="elevation-0"
                >
                    <template v-slot:item.submittedAt="{ item }">
                        {{ formatDate(item.submittedAt) }}
                    </template>
                    <template v-slot:item.answers="{ item }">
                        <v-btn
                            x-small
                            text
                            @click="openAnswers(item)"
                        >
                            {{ $t('surveys.results.individual.table.viewAnswers', { count: item.answers ? item.answers.length : 0 }) }}
                        </v-btn>
                    </template>
                </v-data-table>
            </v-tab-item>
        </v-tabs-items>

        <v-dialog v-model="dialog" max-width="600">
            <v-card v-if="selectedResponse">
                <v-card-title>{{ $t('surveys.results.individual.dialog.title') }}</v-card-title>
                <v-card-text>
                    <div
                        v-for="(answer, i) in selectedResponse.answers"
                        :key="i"
                        class="answer-item"
                    >
                        <div class="answer-question">
                            <span class="answer-number">{{ getQuestionNumber(answer.questionId) }}</span>
                            <span class="answer-title">{{ getQuestionTitle(answer.questionId) }}</span>
                        </div>
                        <div class="answer-value">
                            {{ getAnswerLabel(answer) }}
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="dialog = false">{{ $t('surveys.results.individual.dialog.close') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
    ACTION_GET_OVERVIEW,
    ACTION_GET_SUMMARY,
    ACTION_GET_RESPONSES,
    GET_OVERVIEW,
    GET_SUMMARY,
    GET_RESPONSES,
    GET_STATE_LOADING,
    SURVEYS_M
} from "@/views/surveys/store/types/types.js";

export default {
    name: "surveyResultsPage",

    data() {
        return {
            tab: 0,
            dialog: false,
            selectedResponse: null,
            fromDate: '',
            toDate: '',
            personSearch: '',
            expandedSamples: {},
            sampleLimit: 7
        };
    },

    async mounted() {
        const surveyId = this.$route.query.id;
        if (surveyId) {
            await this.loadResults(surveyId);
        }
    },

    methods: {
        ...mapActions({
            getOverview: `${SURVEYS_M}${ACTION_GET_OVERVIEW}`,
            getSummary: `${SURVEYS_M}${ACTION_GET_SUMMARY}`,
            getResponses: `${SURVEYS_M}${ACTION_GET_RESPONSES}`,
        }),

        async loadResults(surveyId) {
            try {
                await this.getOverview(surveyId);
                await this.getSummary(surveyId);
                await this.applyFilters();
            } catch (error) {
                console.error('Error cargando resultados:', error);
            }
        },

        openAnswers(item) {
            this.selectedResponse = item;
            this.dialog = true;
        },

        async applyFilters() {
            if (!this.isFilterValid) return;
            const surveyId = this.$route.query.id;
            if (!surveyId) return;
            const params = { page: 0, size: 100 };
            if (this.fromDate) params.from = `${this.fromDate}T00:00:00`;
            if (this.toDate) params.to = `${this.toDate}T23:59:59`;
            try {
                await this.getResponses({ surveyId, params });
            } catch (error) {
                console.error('Error cargando respuestas:', error);
            }
        },

        resetFilters() {
            this.fromDate = '';
            this.toDate = '';
            this.personSearch = '';
            this.applyFilters();
        },

        toISODate(value) {
            if (!value) return null;
            return new Date(value).toISOString().split('T')[0];
        },

        showSampleResponses(question) {
            if (!question.sampleResponses) return [];
            if (this.expandedSamples[question.questionId] || question.sampleResponses.length <= this.sampleLimit) {
                return question.sampleResponses;
            }
            return question.sampleResponses.slice(0, this.sampleLimit);
        },

        canShowMore(question) {
            return question.sampleResponses && question.sampleResponses.length > this.sampleLimit;
        },

        toggleSample(questionId) {
            this.$set(this.expandedSamples, questionId, !this.expandedSamples[questionId]);
        },

        formatDate(value) {
            if (!value) return '-';
            return new Date(value).toLocaleString('es-CO');
        },

        histogramPercent(count, histogram) {
            const max = Math.max(...histogram.map(h => h.count));
            return max > 0 ? (count / max) * 100 : 0;
        },

        getQuestionTitle(questionId) {
            const question = this.questionMap[questionId];
            return question ? question.title : this.$t('surveys.results.summary.noQuestionFound');
        },

        getQuestionNumber(questionId) {
            const question = this.questionMap[questionId];
            return question ? question.number : questionId;
        },

        getAnswerLabel(answer) {
            if (answer.valueBoolean === true) return this.$t('surveys.results.individual.dialog.yes');
            if (answer.valueBoolean === false) return this.$t('surveys.results.individual.dialog.no');
            if (answer.valueText) return answer.valueText;
            if (answer.valueNumber !== null && answer.valueNumber !== undefined) return answer.valueNumber;
            if (answer.valueDate) return answer.valueDate;

            if (answer.selectedOptionIds && answer.selectedOptionIds.length) {
                const question = this.questionMap[answer.questionId];
                if (question && question.distribution && question.distribution.length) {
                    const labels = answer.selectedOptionIds.map(id => {
                        const option = question.distribution.find(d => d.optionId === id);
                        return option ? option.label : id;
                    });
                    return labels.join(', ');
                }
                return answer.selectedOptionIds.join(', ');
            }

            return '-';
        }
    },

    computed: {
        ...mapGetters({
            overview: `${SURVEYS_M}${GET_OVERVIEW}`,
            summary: `${SURVEYS_M}${GET_SUMMARY}`,
            responses: `${SURVEYS_M}${GET_RESPONSES}`,
            loading: `${SURVEYS_M}${GET_STATE_LOADING}`,
        }),

        questionMap() {
            if (!this.summary || !this.summary.questions) return {};
            const map = {};
            this.summary.questions.forEach((question, index) => {
                map[question.questionId] = {
                    ...question,
                    number: index + 1
                };
            });
            return map;
        },

        minFromDate() {
            const createdAt = this.overview && (this.overview.createdAt || this.overview.startDate);
            return this.toISODate(createdAt);
        },

        minToDate() {
            return this.fromDate || this.minFromDate;
        },

        fromDateErrors() {
            const errors = [];
            if (!this.fromDate || !this.minFromDate) return errors;
            if (this.fromDate < this.minFromDate) {
                errors.push(this.$t('surveys.results.individual.filters.errors.fromDateMin'));
            }
            return errors;
        },

        toDateErrors() {
            const errors = [];
            if (!this.toDate || !this.fromDate) return errors;
            if (this.toDate < this.fromDate) {
                errors.push(this.$t('surveys.results.individual.filters.errors.toDateMin'));
            }
            return errors;
        },

        isFilterValid() {
            return this.fromDateErrors.length === 0 && this.toDateErrors.length === 0;
        },

        responseHeaders() {
            return [
                { text: this.$t('surveys.results.individual.table.id'), value: 'responseId', align: 'center', sortable: false },
                { text: this.$t('surveys.results.individual.table.date'), value: 'submittedAt', align: 'center', sortable: false },
                { text: this.$t('surveys.results.individual.table.user'), value: 'userId', align: 'center', sortable: false },
                { text: this.$t('surveys.results.individual.table.email'), value: 'email', align: 'center', sortable: false },
                { text: this.$t('surveys.results.individual.table.answers'), value: 'answers', align: 'center', sortable: false }
            ];
        }
    },

    filters: {
        formatNumber(value) {
            if (value === null || value === undefined) return '0';
            return Number(value).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
        }
    }
};
</script>

<style lang="css" scoped>
    .survey-results {
        width: 100%;
        padding: 20px;
        box-sizing: border-box;
    }

    .survey-results__header {
        display: flex;
        align-items: center;
        margin-bottom: 24px;
    }

    .survey-results__header h1 {
        font-family: "Amsi Pro", sans-serif;
        color: var(--v-primary-base);
        font-weight: normal;
        margin: 0;
    }

    .survey-results__title {
        font-family: "Amsi Pro", sans-serif;
        color: var(--v-primary-base);
        font-weight: normal;
        margin-bottom: 16px;
    }

    .distribution-row {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;
    }

    .distribution-row__label {
        min-width: 120px;
        font-size: 14px;
    }

    .distribution-row__bar {
        flex: 1;
    }

    .question-header {
        padding: 4px 0;
    }

    .question-number,
    .answer-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        height: 28px;
        padding: 0 8px;
        background: var(--v-primary-base);
        color: white;
        border-radius: 14px;
        font-size: 14px;
        font-weight: bold;
        margin-right: 10px;
        flex-shrink: 0;
    }

    .question-number {
        width: 28px;
        padding: 0;
        border-radius: 50%;
    }

    .option-number {
        color: var(--v-primary-base);
        font-weight: bold;
        margin-right: 4px;
    }

    .answer-item {
        margin-bottom: 16px;
        padding: 16px;
        background: #f9f9f9;
        border-radius: 8px;
        border-left: 4px solid var(--v-primary-base);
    }

    .answer-question {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
    }

    .answer-title {
        font-weight: 600;
        color: var(--v-primary-base);
        font-size: 16px;
    }

    .answer-value {
        color: #333;
        font-size: 15px;
        padding-left: 38px;
    }
    .sample-responses {
        max-height: 120px;
        overflow-y: auto;
        padding-right: 4px;
    }

    .sample-responses ul {
        margin: 0;
        padding-left: 18px;
    }

    .sample-responses li {
        margin-bottom: 4px;
        word-break: break-word;
    }
</style>
